import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import Card from '../common/Card'
import Button from '../common/Button'
import Input from '../common/Input'
import { PlusIcon, TrashIcon, ArrowPathIcon, LockClosedIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'

/**
 * Manual Payment Schedule Editor Component
 * Allows users to create custom payment schedules with hybrid auto/manual amount distribution
 */
export default function ManualPaymentScheduleEditor({ 
  totalAmount, 
  payments, 
  onChange,
  startDate,
  endDate 
}) {
  const [localPayments, setLocalPayments] = useState(payments || [])

  // Initialize with one payment if empty
  useEffect(() => {
    if (localPayments.length === 0 && totalAmount > 0) {
      setLocalPayments([{
        _id: Date.now(), // Stable unique ID
        payment_number: 1,
        scheduled_date: '',
        amount: totalAmount,
        is_manual_amount: false,
        status: 'scheduled'
      }])
    }
  }, [])

  // Notify parent of changes
  useEffect(() => {
    onChange(localPayments)
  }, [localPayments, onChange])

  /**
   * Smart redistribution algorithm
   * Only redistributes across payments with is_manual_amount: false
   */
  const redistributeAmounts = (paymentsList, total) => {
    // Separate manual and auto payments
    const manualPayments = paymentsList.filter(p => p.is_manual_amount)
    const autoPayments = paymentsList.filter(p => !p.is_manual_amount)
    
    // Calculate locked amount (manual payments)
    const lockedAmount = manualPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
    
    // Calculate remaining amount to distribute
    const remainingAmount = total - lockedAmount
    
    if (autoPayments.length === 0) {
      // All payments are manual, no redistribution needed
      return paymentsList
    }
    
    // Distribute remaining amount evenly across auto payments
    const amountPerAuto = remainingAmount / autoPayments.length
    
    const updated = paymentsList.map(payment => {
      if (payment.is_manual_amount) {
        // Keep manual amounts unchanged
        return payment
      }
      
      // Calculate auto amount
      const autoIndex = autoPayments.indexOf(payment)
      if (autoIndex === autoPayments.length - 1) {
        // Last auto payment gets the adjustment for rounding
        const alreadyDistributed = Math.round(amountPerAuto * (autoPayments.length - 1) * 100) / 100
        return {
          ...payment,
          amount: Math.round((remainingAmount - alreadyDistributed) * 100) / 100
        }
      } else {
        return {
          ...payment,
          amount: Math.round(amountPerAuto * 100) / 100
        }
      }
    })
    
    return updated
  }

  /**
   * Add new payment row
   */
  const handleAddPayment = () => {
    const newPayment = {
      _id: Date.now() + Math.random(), // Stable unique ID
      payment_number: localPayments.length + 1,
      scheduled_date: '',
      amount: 0,
      is_manual_amount: false,
      status: 'scheduled'
    }
    
    const updatedPayments = [...localPayments, newPayment]
    const redistributed = redistributeAmounts(updatedPayments, totalAmount)
    setLocalPayments(redistributed)
  }

  /**
   * Remove payment row
   */
  const handleRemovePayment = (paymentId) => {
    if (localPayments.length === 1) {
      // Don't allow removing the last payment
      return
    }
    
    const updatedPayments = localPayments.filter(p => p._id !== paymentId)
    // Renumber payments
    const renumbered = updatedPayments.map((p, i) => ({
      ...p,
      payment_number: i + 1
    }))
    
    const redistributed = redistributeAmounts(renumbered, totalAmount)
    setLocalPayments(redistributed)
  }

  /**
   * Handle date change
   */
  const handleDateChange = (paymentId, date) => {
    const updated = localPayments.map(p =>
      p._id === paymentId
        ? { ...p, scheduled_date: date }
        : p
    )
    
    // Only sort if all dates are valid
    const allDatesValid = updated.every(p => p.scheduled_date && p.scheduled_date.length === 10)
    
    if (allDatesValid) {
      // Sort by date while maintaining manual/auto flags
      const sorted = [...updated].sort((a, b) => {
        if (!a.scheduled_date) return 1
        if (!b.scheduled_date) return -1
        return new Date(a.scheduled_date) - new Date(b.scheduled_date)
      })
      
      // Renumber after sorting
      const renumbered = sorted.map((p, i) => ({
        ...p,
        payment_number: i + 1
      }))
      
      setLocalPayments(renumbered)
    } else {
      // Don't sort, just update the date
      setLocalPayments(updated)
    }
  }

  /**
   * Handle amount change - marks as manual
   */
  const handleAmountChange = (paymentId, value) => {
    const amount = parseFloat(value) || 0
    
    const updated = localPayments.map(p =>
      p._id === paymentId
        ? { ...p, amount: amount, is_manual_amount: true }
        : p
    )
    
    // Redistribute remaining amount across auto payments only
    const redistributed = redistributeAmounts(updated, totalAmount)
    setLocalPayments(redistributed)
  }

  /**
   * Spread payments evenly - resets all to auto
   */
  const handleSpreadEvenly = () => {
    const resetToAuto = localPayments.map(p => ({
      ...p,
      is_manual_amount: false
    }))
    
    const redistributed = redistributeAmounts(resetToAuto, totalAmount)
    setLocalPayments(redistributed)
  }

  /**
   * Calculate totals and validation
   */
  const { calculatedTotal, difference, isValid } = useMemo(() => {
    const total = localPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
    const roundedTotal = Math.round(total * 100) / 100
    const roundedExpected = Math.round(totalAmount * 100) / 100
    const diff = Math.round((roundedTotal - roundedExpected) * 100) / 100
    const valid = Math.abs(diff) < 0.01
    
    return {
      calculatedTotal: roundedTotal,
      difference: diff,
      isValid: valid
    }
  }, [localPayments, totalAmount])

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Manual Payment Schedule</h3>
            <p className="text-sm text-gray-600 mt-1">
              Add payment dates and amounts. The system will auto-distribute across dates unless you manually edit amounts.
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleSpreadEvenly}
            disabled={localPayments.length === 0}
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Spread Evenly
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="space-y-4">
          {/* Payment Rows */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount (€)</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {localPayments.map((payment) => (
                  <tr key={payment._id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {payment.payment_number}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <Input
                        type="date"
                        value={payment.scheduled_date}
                        onChange={(e) => handleDateChange(payment._id, e.target.value)}
                        min={startDate}
                        max={endDate}
                        className="text-sm"
                        required
                      />
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={payment.amount}
                        onChange={(e) => handleAmountChange(payment._id, e.target.value)}
                        className="text-sm text-right font-medium"
                        required
                      />
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-center">
                      {payment.is_manual_amount ? (
                        <span className="inline-flex items-center text-xs text-blue-600" title="Manually edited">
                          <LockClosedIcon className="h-4 w-4" />
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs text-gray-400" title="Auto-calculated">
                          Auto
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePayment(payment._id)}
                        disabled={localPayments.length === 1}
                        className="text-red-600 hover:text-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add Payment Button */}
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleAddPayment}
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Payment
            </Button>
          </div>

          {/* Validation Summary */}
          <div className={`p-4 rounded-lg border-2 ${
            isValid 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start gap-3">
              {isValid ? (
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <ExclamationCircleIcon className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${
                    isValid ? 'text-green-900' : 'text-red-900'
                  }`}>
                    Payment Total:
                  </span>
                  <span className={`text-lg font-bold ${
                    isValid ? 'text-green-900' : 'text-red-900'
                  }`}>
                    €{calculatedTotal.toLocaleString('en-US', { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-600">Contract Amount:</span>
                  <span className="text-sm text-gray-900">
                    €{totalAmount.toLocaleString('en-US', { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                  </span>
                </div>
                {!isValid && (
                  <div className="mt-2 pt-2 border-t border-red-200">
                    <p className={`text-sm font-medium ${
                      difference > 0 ? 'text-red-900' : 'text-orange-900'
                    }`}>
                      {difference > 0 ? (
                        <>€{Math.abs(difference).toFixed(2)} over budget</>
                      ) : (
                        <>€{Math.abs(difference).toFixed(2)} remaining to allocate</>
                      )}
                    </p>
                    <p className="text-xs text-red-700 mt-1">
                      Adjust payment amounts or use "Spread Evenly" to match the contract total.
                    </p>
                  </div>
                )}
                {isValid && (
                  <p className="text-xs text-green-700 mt-1">
                    ✓ Payment total matches contract amount exactly
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="text-xs font-medium text-gray-700 mb-2">How it works:</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Add payment dates first - amounts will auto-distribute evenly</li>
              <li>• Edit any amount to lock it (marked with <LockClosedIcon className="h-3 w-3 inline text-blue-600" />)</li>
              <li>• Locked amounts won't change when you add/remove payments</li>
              <li>• "Spread Evenly" resets all amounts to auto-calculated</li>
              <li>• Total must exactly match contract amount to save</li>
            </ul>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

ManualPaymentScheduleEditor.propTypes = {
  totalAmount: PropTypes.number.isRequired,
  payments: PropTypes.arrayOf(
    PropTypes.shape({
      payment_number: PropTypes.number,
      scheduled_date: PropTypes.string,
      amount: PropTypes.number,
      is_manual_amount: PropTypes.bool,
      status: PropTypes.string
    })
  ),
  onChange: PropTypes.func.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string
}

ManualPaymentScheduleEditor.defaultProps = {
  payments: [],
  startDate: '',
  endDate: ''
}